import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, Eye, EyeOff, CheckCircle, User, Building2, Users, Home, ArrowLeft, FileText, Shield, Download, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1 = account type, 2 = details
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    confirmPassword: '',
    accountType: 'user' // user, business, both
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEmailAlreadyRegistered, setIsEmailAlreadyRegistered] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Consent checkboxes state
  const [consents, setConsents] = useState({
    terms: false,
    privacy: false,
    popia: false
  });

  useEffect(() => {
    // Check URL params for account type
    const type = searchParams.get('type');
    if (type === 'business') {
      setFormData(prev => ({ ...prev, accountType: 'business' }));
    }
  }, [searchParams]);

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    return null;
  };

  const handleResendVerification = async () => {
    if (!formData.email) return;
    
    setResendLoading(true);
    setResendSuccess(false);
    
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, {
        email: formData.email
      });
      
      // Check if email is already verified
      const message = response.data?.message || '';
      if (message.toLowerCase().includes('already verified')) {
        setError('Email is already verified. You can log in.');
        setIsEmailAlreadyRegistered(false);
      } else {
        setResendSuccess(true);
        setError('');
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string' && errorDetail.includes('already verified')) {
        setError('Email is already verified. You can log in.');
        setIsEmailAlreadyRegistered(false);
      } else {
        setError('Failed to resend verification email. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check all consents
    if (!consents.terms || !consents.privacy || !consents.popia) {
      setError('Please accept all terms, privacy policy, and POPIA consent to continue');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    setIsEmailAlreadyRegistered(false);
    setResendSuccess(false);

    try {
      await axios.post(`${API}/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        account_type: formData.accountType,
        consents: {
          terms_accepted: consents.terms,
          privacy_accepted: consents.privacy,
          popia_accepted: consents.popia,
          accepted_at: new Date().toISOString()
        }
      });
      setSuccess(true);
    } catch (err) {
      // Handle Pydantic validation errors (array of objects) or string errors
      const errorDetail = err.response?.data?.detail;
      
      // Check if email is already registered
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : '';
      if (errorMessage.toLowerCase().includes('email already registered')) {
        setIsEmailAlreadyRegistered(true);
        setError('This email is already registered.');
      } else if (Array.isArray(errorDetail)) {
        // Pydantic v2 validation error format: [{type, loc, msg, input, url}, ...]
        setError(errorDetail.map(e => e.msg || e.message || 'Validation error').join('. '));
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        setError(errorDetail.msg || errorDetail.message || 'Registration failed. Please try again.');
      } else {
        setError(errorDetail || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const allConsentsAccepted = consents.terms && consents.privacy && consents.popia;

  const accountTypes = [
    {
      type: 'user',
      icon: User,
      title: 'Customer Account',
      description: 'Browse and book equipment from vendors',
      features: ['Browse all vendors', 'Book equipment & PA systems', 'Track your bookings', 'Rate services']
    },
    {
      type: 'business',
      icon: Building2,
      title: 'Business Account',
      description: 'List your rental business on our marketplace',
      features: ['Create business profile', 'List equipment & packages', 'Manage bookings', 'Receive payments']
    },
    {
      type: 'both',
      icon: Users,
      title: 'Both',
      description: 'Use the platform as both customer and vendor',
      features: ['All customer features', 'All business features', 'Switch between profiles', 'Full platform access']
    }
  ];

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <Card className="w-full max-w-md shadow-2xl border-gray-700 bg-gray-800">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-4 bg-gradient-to-br from-green-500 to-green-600 rounded-full">
                <CheckCircle className="h-12 w-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-green-400">Registration Successful!</CardTitle>
            <CardDescription className="text-base text-gray-400">
              We&apos;ve sent a verification email to <strong className="text-white">{formData.email}</strong>. 
              Please check your inbox and click the verification link to activate your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.accountType !== 'user' && (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-200">
                  <strong>Business Account:</strong> After verifying your email, you&apos;ll need to complete 
                  your business profile and submit verification documents before you can start listing equipment.
                </p>
              </div>
            )}
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold"
            >
              Go to Login
            </Button>
            <p className="text-center text-sm text-gray-500">
              Didn&apos;t receive the email? Check your spam folder or contact support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4 py-8">
      {/* Back to Home Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/')}
        className="fixed top-4 left-4 text-gray-400 hover:text-white hover:bg-gray-800"
        data-testid="back-to-home-btn"
      >
        <Home className="h-4 w-4 mr-2" />
        Back to Home
      </Button>
      
      <Card className="w-full max-w-2xl shadow-2xl border-gray-700 bg-gray-800">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img 
              src="/logo.png"
              alt="Gumba Logo"
              className="h-20 object-contain"
            />
          </div>
          <CardTitle className="text-3xl font-bold text-white">
            {step === 1 ? 'Choose Account Type' : 'Create Your Account'}
          </CardTitle>
          <CardDescription className="text-base text-gray-400">
            {step === 1 
              ? 'Select how you want to use the Gumba Marketplace'
              : `Creating a ${formData.accountType === 'both' ? 'dual' : formData.accountType} account`
            }
          </CardDescription>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2">
            <div className={`w-8 h-2 rounded-full ${step >= 1 ? 'bg-yellow-500' : 'bg-gray-600'}`} />
            <div className={`w-8 h-2 rounded-full ${step >= 2 ? 'bg-yellow-500' : 'bg-gray-600'}`} />
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {step === 1 ? (
            // Step 1: Account Type Selection
            <div className="space-y-4">
              <div className="grid gap-4">
                {accountTypes.map((account) => (
                  <div
                    key={account.type}
                    className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      formData.accountType === account.type
                        ? 'border-yellow-500 bg-yellow-500/10'
                        : 'border-gray-600 hover:border-gray-500 bg-gray-700'
                    }`}
                    onClick={() => setFormData({ ...formData, accountType: account.type })}
                    data-testid={`account-type-${account.type}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-full ${
                        formData.accountType === account.type 
                          ? 'bg-yellow-500 text-black' 
                          : 'bg-gray-600 text-gray-300'
                      }`}>
                        <account.icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-white">{account.title}</h3>
                        <p className="text-sm text-gray-400 mb-2">{account.description}</p>
                        <ul className="grid grid-cols-2 gap-1">
                          {account.features.map((feature, idx) => (
                            <li key={idx} className="text-xs text-gray-400 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.accountType === account.type
                          ? 'border-yellow-500 bg-yellow-500'
                          : 'border-gray-500'
                      }`}>
                        {formData.accountType === account.type && (
                          <CheckCircle className="h-4 w-4 text-white" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={() => setStep(2)}
                className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold py-6"
                data-testid="continue-btn"
              >
                Continue
              </Button>
              
              <p className="text-center text-sm text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="text-yellow-500 hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          ) : (
            // Step 2: Account Details
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="name-input"
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="password-input"
                    className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Must be at least 8 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  data-testid="confirm-password-input"
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                />
              </div>

              {/* Legal Consent Checkboxes */}
              <div className="space-y-3 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                <p className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-yellow-500" />
                  Legal Agreements
                </p>
                
                {/* Terms and Conditions */}
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="terms"
                    checked={consents.terms}
                    onCheckedChange={(checked) => setConsents({ ...consents, terms: checked })}
                    className="mt-0.5 border-gray-500 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                    data-testid="terms-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="terms" className="text-sm text-gray-300 cursor-pointer">
                      I accept the{' '}
                      <a 
                        href="/docs/Gumba_Terms_and_Conditions.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-yellow-500 hover:underline inline-flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms and Conditions
                        <Download className="h-3 w-3" />
                      </a>
                      {' '}including the refund policy
                    </Label>
                  </div>
                </div>

                {/* Privacy Policy */}
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="privacy"
                    checked={consents.privacy}
                    onCheckedChange={(checked) => setConsents({ ...consents, privacy: checked })}
                    className="mt-0.5 border-gray-500 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                    data-testid="privacy-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="privacy" className="text-sm text-gray-300 cursor-pointer">
                      I have read and accept the{' '}
                      <a 
                        href="/docs/Gumba_Privacy_Policy.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-yellow-500 hover:underline inline-flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                        <Download className="h-3 w-3" />
                      </a>
                    </Label>
                  </div>
                </div>

                {/* POPIA Consent */}
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="popia"
                    checked={consents.popia}
                    onCheckedChange={(checked) => setConsents({ ...consents, popia: checked })}
                    className="mt-0.5 border-gray-500 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                    data-testid="popia-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="popia" className="text-sm text-gray-300 cursor-pointer">
                      I consent to the processing of my personal information as described in the{' '}
                      <a 
                        href="/docs/Gumba_POPIA_Policy.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-yellow-500 hover:underline inline-flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        POPIA Policy
                        <Download className="h-3 w-3" />
                      </a>
                    </Label>
                  </div>
                </div>
              </div>

              {/* Error and Resend Verification */}
              {error && (
                <div className="p-3 bg-red-900/50 text-red-400 rounded-lg text-sm border border-red-800" data-testid="register-error">
                  {error}
                  {isEmailAlreadyRegistered && !resendSuccess && (
                    <div className="mt-2 pt-2 border-t border-red-800">
                      <p className="text-gray-300 mb-2">
                        Haven&apos;t verified your email yet?
                      </p>
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendLoading}
                        className="inline-flex items-center gap-2 text-yellow-500 hover:text-yellow-400 font-medium disabled:opacity-50"
                        data-testid="resend-verification-btn"
                      >
                        {resendLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="h-4 w-4" />
                            Resend Verification Email
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  {error.toLowerCase().includes('already verified') && (
                    <div className="mt-2 pt-2 border-t border-red-800">
                      <Link 
                        to="/login" 
                        className="inline-flex items-center gap-2 text-yellow-500 hover:text-yellow-400 font-medium"
                      >
                        Go to Login
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Resend Success Message */}
              {resendSuccess && (
                <div className="p-3 bg-green-900/50 text-green-400 rounded-lg text-sm border border-green-800" data-testid="resend-success">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    <span>Verification email sent! Please check your inbox.</span>
                  </div>
                  <p className="mt-2 text-gray-300 text-xs">
                    Already verified?{' '}
                    <Link to="/login" className="text-yellow-500 hover:underline">
                      Go to Login
                    </Link>
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !allConsentsAccepted}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gray-800 text-gray-400">or continue with</span>
            </div>
          </div>

          {/* Google Sign In Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full border-gray-600 bg-gray-700 hover:bg-gray-600 text-white py-6"
            onClick={async () => {
              try {
                const response = await axios.get(`${API}/auth/google/url`);
                window.location.href = response.data.auth_url;
              } catch (err) {
                setError('Failed to initialize Google Sign-In');
              }
            }}
            data-testid="google-register-btn"
          >
            <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
